import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, Trash2, Video, Image, AlertCircle, ExternalLink, Clock, Layers, Calendar, Library } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';

function Notifications() {
    const navigate = useNavigate();
    const {
        notifications,
        unreadCount,
        loading,
        markAsRead,
        markAllAsRead,
        clearNotification
    } = useNotifications();

    const [filter, setFilter] = useState('all'); // 'all', 'unread', 'generation'

    const filteredNotifications = notifications.filter(notification => {
        if (filter === 'unread') return !notification.read;
        if (filter === 'generation') return notification.type?.includes('generation');
        return true;
    });

    const getNotificationIcon = (notification) => {
        switch (notification.type) {
            case 'generation_complete':
                return notification.data?.type === 'video'
                    ? <Video size={20} style={{ color: 'var(--color-success)' }} />
                    : <Image size={20} style={{ color: 'var(--color-success)' }} />;
            case 'generation_failed':
                return <AlertCircle size={20} style={{ color: 'var(--color-error)' }} />;
            case 'bulk_generation_complete':
                return <Layers size={20} style={{ color: 'var(--color-success)' }} />;
            case 'bulk_generation_failed':
                return <AlertCircle size={20} style={{ color: 'var(--color-error)' }} />;
            default:
                return <Bell size={20} style={{ color: 'var(--color-primary)' }} />;
        }
    };

    const formatTimeAgo = (date) => {
        if (!date) return '';
        const now = new Date();
        const then = new Date(date);
        const diffMs = now - then;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return then.toLocaleDateString();
    };

    const handleNotificationClick = async (notification, action = null) => {
        // Mark as read
        if (!notification.read) {
            await markAsRead(notification.id);
        }

        // Handle bulk generation notifications with actions
        if (notification.type === 'bulk_generation_complete') {
            if (action === 'schedule' || action === 'resume_scheduling') {
                // Navigate to Strategy Hub with resumePlanId to open BulkContentPlanner in resume mode
                const planId = notification.data?.planId;
                if (planId) {
                    navigate(`/strategy?resumePlanId=${planId}`);
                } else {
                    navigate('/strategy');
                }
            } else if (action === 'library') {
                // Navigate to Asset Library filtered by planId
                navigate(`/library?planId=${notification.data?.planId}`);
            } else {
                // Default action - go to strategy with resume context
                const planId = notification.data?.planId;
                if (planId) {
                    navigate(`/strategy?resumePlanId=${planId}`);
                } else {
                    navigate(`/library?planId=${notification.data?.planId}`);
                }
            }
            return;
        }

        // Navigate to relevant page
        if (notification.chatId) {
            navigate(`/conversational?chatId=${notification.chatId}`);
        } else if (notification.data?.url) {
            // Open media in new tab or show in modal
            window.open(notification.data.url, '_blank');
        }
    };

    return (
        <div style={{ padding: 'var(--space-6)', maxWidth: '900px', margin: '0 auto' }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 'var(--space-6)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                    <Bell size={32} style={{ color: 'var(--color-primary)' }} />
                    <div>
                        <h1 style={{
                            fontSize: 'var(--font-size-2xl)',
                            fontWeight: 700,
                            color: 'var(--color-text-primary)',
                            margin: 0
                        }}>
                            Notifications
                        </h1>
                        <p style={{
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--color-text-tertiary)',
                            margin: 0
                        }}>
                            {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'}
                        </p>
                    </div>
                </div>

                {unreadCount > 0 && (
                    <button
                        onClick={markAllAsRead}
                        className="btn btn-primary"
                        style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}
                    >
                        <CheckCheck size={16} />
                        Mark all as read
                    </button>
                )}
            </div>

            {/* Filters */}
            <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-6)' }}>
                {[
                    { key: 'all', label: 'All' },
                    { key: 'unread', label: 'Unread' },
                    { key: 'generation', label: 'Generation Jobs' }
                ].map(({ key, label }) => (
                    <button
                        key={key}
                        onClick={() => setFilter(key)}
                        className={`btn ${filter === key ? 'btn-primary' : 'btn-ghost'}`}
                        style={{
                            padding: 'var(--space-2) var(--space-4)',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 500
                        }}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {/* Notifications List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: 'var(--space-12)' }}>
                        <div className="loading-spinner" style={{
                            width: '32px',
                            height: '32px',
                            border: '2px solid var(--color-primary)',
                            borderTopColor: 'transparent',
                            borderRadius: '50%',
                            margin: '0 auto var(--space-4)',
                            animation: 'spin 1s linear infinite'
                        }} />
                        <p style={{ color: 'var(--color-text-tertiary)' }}>Loading notifications...</p>
                    </div>
                ) : filteredNotifications.length === 0 ? (
                    <div style={{
                        textAlign: 'center',
                        padding: 'var(--space-12)',
                        background: 'var(--color-surface)',
                        borderRadius: 'var(--radius-xl)',
                        border: '1px solid var(--color-border)'
                    }}>
                        <Bell size={48} style={{ color: 'var(--color-text-quaternary)', margin: '0 auto var(--space-4)' }} />
                        <h3 style={{
                            fontSize: 'var(--font-size-lg)',
                            fontWeight: 500,
                            color: 'var(--color-text-secondary)',
                            marginBottom: 'var(--space-2)'
                        }}>
                            No notifications
                        </h3>
                        <p style={{ color: 'var(--color-text-tertiary)' }}>
                            {filter === 'unread'
                                ? "You're all caught up!"
                                : "You don't have any notifications yet."}
                        </p>
                    </div>
                ) : (
                    filteredNotifications.map(notification => (
                        <div
                            key={notification.id}
                            onClick={() => handleNotificationClick(notification)}
                            className="notification-card"
                            style={{
                                position: 'relative',
                                padding: 'var(--space-4)',
                                borderRadius: 'var(--radius-xl)',
                                border: `1px solid ${notification.read ? 'var(--color-border)' : 'var(--color-primary-light)'}`,
                                background: notification.read ? 'var(--color-surface)' : 'var(--color-surface-hover)',
                                cursor: 'pointer',
                                transition: 'all var(--transition-fast)'
                            }}
                        >
                            {/* Unread indicator */}
                            {!notification.read && (
                                <div style={{
                                    position: 'absolute',
                                    top: 'var(--space-4)',
                                    right: 'var(--space-4)',
                                    width: '8px',
                                    height: '8px',
                                    background: 'var(--color-primary)',
                                    borderRadius: '50%'
                                }} />
                            )}

                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-4)' }}>
                                {/* Icon */}
                                <div style={{
                                    padding: 'var(--space-2)',
                                    borderRadius: 'var(--radius-lg)',
                                    background: notification.type === 'generation_complete'
                                        ? 'rgba(16, 185, 129, 0.1)'
                                        : notification.type === 'generation_failed'
                                            ? 'rgba(239, 68, 68, 0.1)'
                                            : 'rgba(124, 58, 237, 0.1)'
                                }}>
                                    {getNotificationIcon(notification)}
                                </div>

                                {/* Content */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <h3 style={{
                                        fontWeight: 500,
                                        color: 'var(--color-text-primary)',
                                        marginBottom: 'var(--space-1)'
                                    }}>
                                        {notification.title}
                                    </h3>
                                    <p style={{
                                        fontSize: 'var(--font-size-sm)',
                                        color: 'var(--color-text-secondary)',
                                        marginBottom: 'var(--space-2)'
                                    }}>
                                        {notification.message}
                                    </p>

                                    {/* Metadata */}
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-4)',
                                        fontSize: 'var(--font-size-xs)',
                                        color: 'var(--color-text-tertiary)'
                                    }}>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                                            <Clock size={12} />
                                            {formatTimeAgo(notification.createdAt)}
                                        </span>

                                        {notification.data?.prompt && (
                                            <span style={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                maxWidth: '200px'
                                            }}>
                                                "{notification.data.prompt.substring(0, 50)}..."
                                            </span>
                                        )}
                                    </div>

                                    {/* Preview for completed generations */}
                                    {notification.type === 'generation_complete' && notification.data?.url && (
                                        <div style={{
                                            marginTop: 'var(--space-3)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-2)'
                                        }}>
                                            {notification.data.type === 'video' ? (
                                                <div style={{
                                                    width: '64px',
                                                    height: '64px',
                                                    background: 'var(--color-bg-secondary)',
                                                    borderRadius: 'var(--radius-lg)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}>
                                                    <Video size={24} style={{ color: 'var(--color-text-tertiary)' }} />
                                                </div>
                                            ) : (
                                                <img
                                                    src={notification.data.url}
                                                    alt="Generated"
                                                    style={{
                                                        width: '64px',
                                                        height: '64px',
                                                        objectFit: 'cover',
                                                        borderRadius: 'var(--radius-lg)'
                                                    }}
                                                />
                                            )}
                                            <span style={{
                                                color: 'var(--color-primary)',
                                                fontSize: 'var(--font-size-sm)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 'var(--space-1)'
                                            }}>
                                                View {notification.data.type}
                                                <ExternalLink size={12} />
                                            </span>
                                        </div>
                                    )}

                                    {/* Action buttons for bulk generation */}
                                    {notification.type === 'bulk_generation_complete' && (
                                        <div style={{
                                            marginTop: 'var(--space-3)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-2)'
                                        }}>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleNotificationClick(notification, 'schedule');
                                                }}
                                                className="btn btn-primary btn-sm"
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 'var(--space-1)',
                                                    fontSize: 'var(--font-size-sm)',
                                                    padding: 'var(--space-2) var(--space-3)'
                                                }}
                                            >
                                                <Calendar size={14} />
                                                Continue Scheduling
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleNotificationClick(notification, 'library');
                                                }}
                                                className="btn btn-secondary btn-sm"
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 'var(--space-1)',
                                                    fontSize: 'var(--font-size-sm)',
                                                    padding: 'var(--space-2) var(--space-3)'
                                                }}
                                            >
                                                <Library size={14} />
                                                View in Library
                                            </button>
                                            {notification.data?.successCount > 0 && (
                                                <span style={{
                                                    fontSize: 'var(--font-size-xs)',
                                                    color: 'var(--color-text-tertiary)',
                                                    marginLeft: 'var(--space-2)'
                                                }}>
                                                    {notification.data.successCount} creatives ready
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Actions */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                    {!notification.read && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                markAsRead(notification.id);
                                            }}
                                            className="btn btn-icon btn-ghost"
                                            title="Mark as read"
                                            style={{ padding: 'var(--space-2)' }}
                                        >
                                            <Check size={16} />
                                        </button>
                                    )}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            clearNotification(notification.id);
                                        }}
                                        className="btn btn-icon btn-ghost"
                                        title="Delete"
                                        style={{
                                            padding: 'var(--space-2)',
                                            color: 'var(--color-text-tertiary)'
                                        }}
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <style>{`
                .notification-card:hover {
                    border-color: var(--color-primary) !important;
                    transform: translateY(-1px);
                }
                .notification-card:hover .btn-icon {
                    opacity: 1;
                }
                .notification-card .btn-icon:hover {
                    color: var(--color-primary);
                    background: rgba(124, 58, 237, 0.1);
                }
                .notification-card .btn-icon:last-child:hover {
                    color: var(--color-error);
                    background: rgba(239, 68, 68, 0.1);
                }
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
}

export default Notifications;
